import React, {useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {Container, Button, Form, Dropdown, Nav, Row, Spinner} from 'react-bootstrap';
import ReactTooltip from 'react-tooltip';
import {useHistory} from 'react-router-dom';
import {Helmet} from 'react-helmet';
import Navbar from '../../../components/Navbar';
import Sidebar from '../../../components/Sidebar';
import Footer from '../../../components/Footer';
import CourseCard from './CourseCard';
import Filter from './ConferenceFilter';
import {
  Main,
  CatalogList,
  CourseFilter,
  ChipText,
  Header,
  PaginationContainer,
  NavItem,
  Tab,
  Link,
  FilterContainer,
} from '../elements/catalog';
// import ScreenLoader from '../../../components/ScreenLoader';
import close from '../elements/close-chip.svg';
import {useStore} from '../../../store';
import AlertComponent from '../../../components/AlertComponent';
import Icon from '../elements/bars.svg';
import FilterIcon from '../elements/filter.png';
import ScreenLoader from '../../../components/ScreenLoader';
import PageBottom from '../../../components/PageBottom';
import {useInfiniteScroll} from '../../../hooks/useInfiniteScroll';

// Conference Catalog
const ConferenceCatalog = observer(() => {
  const {conferenceStore, alertStore} = useStore();
  const {
    professionsList,
    listProfessions,
    conferenceSpecialtiesList,
    listConferenceSpecialties,
    organizationsList,
    listOrganizations,
    conferencesList,
    loading,
    selectedSpecialtiesList,
    selectedProfessions,
    selectedOrganizationList,
    setSort,
    sortBy,
    sortOrder,
    conferenceCount,
    count,
    pageIncrement,
    pageDecrement,
    changeCount,
    listConferences,
    onRemoveSpecialty,
    onResetProfession,
    onRemoveOrganization,
    onCreateBookmark,
    onDeleteBookmark,
    playlist,
    getAllPlaylists,
    setSearch,
    searchText,
    resetSearch,
    setSelectedProfessions,
    setSelectedSpecialities,
    setSelectedOrganizations,
    setCME,
    scrollCatalog,
    loadMoreConferences,
    isConferenceLoadingMore,
  } = conferenceStore;
  const {message, status, resetAlert} = alertStore;
  const location = useHistory();
  const [searchInputValue, setSearchInputValue] = useState(searchText);
  const [showFilter, setShowFilter] = useState(false);

  const hasMoreConferences = conferencesList.length < conferenceCount;
  const sentinelRef = useInfiniteScroll(loadMoreConferences, {
    enabled: hasMoreConferences && !loading,
  });
  const loadedEnd = Math.min(conferencesList.length, conferenceCount);

  useEffect(() => {
    setSelectedOrganizations([], false);
    setSelectedProfessions([], false);
    setSelectedSpecialities([], false);
    setCME(false);
    resetSearch();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    getAllPlaylists();
  }, [getAllPlaylists]);

  useEffect(() => {
    listProfessions();
    listConferenceSpecialties();
    listOrganizations();
  }, [listProfessions, listConferenceSpecialties, listOrganizations]);

  useEffect(() => {
    listConferences();
  }, [sortBy, sortOrder, count, searchText, listConferences]);

  const selectOptions = (selectedIDs, type) => {
    if (type === 1) {
      setSelectedProfessions(selectedIDs, false);
    } else if (type === 2) {
      setSelectedSpecialities(selectedIDs, false);
    } else {
      setSelectedOrganizations(selectedIDs, false);
    }
  };

  const clearFunc = () => {
    selectOptions([], 0);
    selectOptions([], 1);
    selectOptions([], 2);
    setCME();
    resetSearch();
  };

  const removeProfession = () => {
    onResetProfession();
  };

  const removeSpecialty = item => {
    onRemoveSpecialty(item.id);
  };

  const removeOrganization = item => {
    onRemoveOrganization(item.id);
  };

  // eslint-disable-next-line
  const onHandleBookmark = item => {
    const user = localStorage.getItem('user');
    if (user) {
      if (item.bookmark?.length) {
        onDeleteBookmark(item);
      } else {
        onCreateBookmark(item.id);
      }
    } else {
      location.push('/login');
    }
  };

  useEffect(() => {
    ReactTooltip.rebuild();
  });

  const onSearchInputChange = event => {
    const value = event?.target?.value;
    setSearchInputValue(value);
    if (!value) {
      resetSearch();
    }
  };

  const submitSearch = () => {
    if (searchInputValue) {
      setSearch(searchInputValue);
    }
  };

  const onSearchInputKeyUp = event => {
    if (searchInputValue && searchInputValue.length <= 200 && event.key === 'Enter') {
      submitSearch();
    }
  };

  const [profession, setProfession] = useState(null);
  useEffect(() => {
    setProfession(professionsList.find(x => x.id === parseInt(selectedProfessions[0], 10)));
  }, [professionsList, selectedProfessions, setProfession]);

  const [windowSize, setWindowSize] = useState(window.innerWidth);

  const resizeWindow = () => {
    setWindowSize(window.innerWidth);
  };

  useEffect(() => {
    resizeWindow();
    window.addEventListener('resize', resizeWindow);
    return () => window.removeEventListener('resize', resizeWindow);
  }, []);

  useEffect(() => {
    const scrollEvent = e => {
      const {scrollTop, clientHeight, scrollHeight} = e.target.scrollingElement;
      if (Math.round(scrollHeight - scrollTop) <= Math.round(clientHeight)) {
        scrollCatalog();
      }
    };
    if (windowSize <= 576) {
      window.addEventListener('scroll', e => scrollEvent(e));
      window.setTimeout(() => window.removeEventListener('scroll', e => scrollEvent(e)), 1000);
    }
  }, [scrollCatalog, windowSize]);

  return (
    <div id="conferences" className="d-flex flex-column flex-fill">
      <ReactTooltip place="top" effect="solid" id="Catalog Conference" />
      <Helmet
        title="GIBLIB | Catalog"
        meta={[
          {
            name: 'description',
            content:
              "Stream surgical videos and medical lectures from subject matter experts at the leading academic medical institutions on demand with unlimited access by being a subscriber to GIBLIB today. Earn CME with GIBLIB's premium plan while learning from the world's best doctors, online.",
          },
          {
            property: 'og:title',
            content: 'GIBLIB | Catalog',
          },
          {
            name: 'twitter:card',
            content: 'summary',
          },
          {
            name: 'twitter:image',
            content: 'https://s3.us-west-1.amazonaws.com/v4.0-assets/GBLB_Logo_Horizontal_Notag_White_FullColor.png',
          },
          {
            name: 'image',
            property: 'og:image',
            content: 'https://s3.us-west-1.amazonaws.com/v4.0-assets/GBLB_Logo_Horizontal_Notag_White_FullColor.png',
          },
          {
            property: 'og:url',
            content: window.location.origin + window.location.pathname,
          },
        ]}
      />
      <Navbar />
      <div className="d-flex">
        <Sidebar />
        <Main role="main">
          <Header>
            <Container>
              <h5>
                <img src={Icon} alt="icon" />
                Catalog
              </h5>
            </Container>
          </Header>
          {message ? <AlertComponent message={message} status={status} click={() => resetAlert()} /> : null}
          <Container>
            <Tab>
              <Nav>
                <NavItem className="nav-item">
                  <Link className="nav-link a_showBookmarks" activeClassName="active" to="/catalog/videos">
                    Videos
                  </Link>
                </NavItem>
                {/* <NavItem className="nav-item">
                  <Link className="nav-link a_showBookmarks" activeClassName="active" to="/catalog/courses">
                    Courses
                  </Link>
                </NavItem> */}
                <NavItem className="nav-item">
                  <Link className="nav-link a_showBookmarks" activeClassName="active" to="/catalog/conferences">
                    Conferences
                  </Link>
                </NavItem>
              </Nav>
            </Tab>
            <Row>
              <CourseFilter className="col-md-4">
                <h5>
                  <PaginationContainer className="sort-mobile">
                    <Dropdown>
                      <Dropdown.Toggle variant="basic" id="sort" className="a_sort">
                        Sort by
                      </Dropdown.Toggle>

                      <Dropdown.Menu onChange={e => setSort(e.target.value)}>
                        <Form.Check
                          type="radio"
                          label="Name (A-Z)"
                          id="alphabet-asc"
                          value="Name (A-Z)"
                          name="sort"
                          checked={sortOrder === 'ASC' && sortBy === 'title'}
                        />
                        <Form.Check
                          type="radio"
                          label="Name (Z-A)"
                          id="alphabet-desc"
                          value="Name (Z-A)"
                          name="sort"
                          checked={sortOrder === 'DESC' && sortBy === 'title'}
                        />
                        <Form.Check
                          type="radio"
                          label="Date Added (Newest)"
                          id="newest"
                          value="Date Added (Newest)"
                          name="sort"
                          checked={sortOrder === 'DESC' && sortBy === 'created_at'}
                        />
                        <Form.Check
                          type="radio"
                          label="Date Added (Oldest)"
                          id="oldest"
                          value="Date Added (Oldest)"
                          name="sort"
                          checked={sortOrder === 'ASC' && sortBy === 'created_at'}
                        />
                      </Dropdown.Menu>
                    </Dropdown>
                  </PaginationContainer>
                  <Button className={`filter-btn ${!showFilter ? 'show' : 'hide'}`} onClick={() => setShowFilter(true)}>
                    <img src={FilterIcon} alt="" />
                  </Button>
                  <div className="filter-img">
                    <img src={FilterIcon} alt="" />
                    Filter
                  </div>
                </h5>
                <FilterContainer className={showFilter ? 'show' : 'hide'}>
                  <Form.Row className="justify-content-between m-0 px-3 hide-mobile">
                    <span>
                      {conferenceCount === 1 ? 'Result' : 'Results'} {conferenceCount}
                    </span>
                    <Button onClick={clearFunc} className="clear-btn" type="reset">
                      Reset
                    </Button>
                  </Form.Row>
                  <Form.Row className="justify-content-between m-0 px-3 show-mobile">
                    <h5>
                      <Button className="filter-btn" onClick={() => setShowFilter(false)}>
                        <img src={FilterIcon} alt="" />
                      </Button>
                      Filter
                    </h5>
                    <div>
                      <Button onClick={clearFunc} className="clear-btn" type="reset">
                        Reset
                      </Button>
                      <Button onClick={() => setShowFilter(false)} className="closeFilter">
                        <img alt="close" src={close} />
                      </Button>
                    </div>
                  </Form.Row>
                  <hr />
                  <div className="d-flex container flex-wrap mb-3">
                    {profession ? (
                      <ChipText>
                        {profession.name}
                        <span>
                          <button type="button" className="bg-transparent border-0" onClick={() => removeProfession()}>
                            <img alt="close" src={close} />
                          </button>
                        </span>
                      </ChipText>
                    ) : null}
                    {selectedSpecialtiesList.map(item => (
                      <ChipText>
                        {item.name}
                        <span>
                          <button
                            type="button"
                            className="bg-transparent border-0"
                            onClick={() => removeSpecialty(item)}
                          >
                            <img alt="close" src={close} />
                          </button>
                        </span>
                      </ChipText>
                    ))}
                    {selectedOrganizationList.map(item => (
                      <ChipText>
                        {item.name}
                        <span>
                          <button
                            type="button"
                            className="bg-transparent border-0"
                            onClick={() => removeOrganization(item)}
                          >
                            <img alt="close" src={close} />
                          </button>
                        </span>
                      </ChipText>
                    ))}
                  </div>
                  <Form.Row className="input-group form-inline m-0">
                    <div className="input-group-prepend">
                      <button className="btn btn-outline-secondary search-button" type="submit" onClick={submitSearch}>
                        <i className="fas fa-search" />
                      </button>
                    </div>
                    <input
                      id="search-catalog"
                      defaultValue={searchInputValue}
                      className="form-control search control-bottom"
                      type="search"
                      placeholder="Search Catalog"
                      aria-label="search"
                      name="search"
                      onChange={onSearchInputChange}
                      onKeyUp={onSearchInputKeyUp}
                      autoComplete="off"
                    />
                  </Form.Row>
                  <Filter
                    professionFilter={professionsList}
                    specialityFilter={conferenceSpecialtiesList}
                    organizationFilter={organizationsList}
                  />
                </FilterContainer>
              </CourseFilter>
              <CatalogList className="col-md-8">
                <Container id="course_container" className="pr-0">
                  <div className="pb-3 d-flex justify-content-end">
                    <PaginationContainer>
                      <Dropdown>
                        <Dropdown.Toggle variant="basic" id="sort" className="a_sort">
                          Sort by
                        </Dropdown.Toggle>

                        <Dropdown.Menu onChange={e => setSort(e.target.value)}>
                          <Form.Check
                            type="radio"
                            label="Name (A-Z)"
                            id="alphabet-asc"
                            value="Name (A-Z)"
                            name="sort"
                            checked={sortOrder === 'ASC' && sortBy === 'title'}
                          />
                          <Form.Check
                            type="radio"
                            label="Name (Z-A)"
                            id="alphabet-desc"
                            value="Name (Z-A)"
                            name="sort"
                            checked={sortOrder === 'DESC' && sortBy === 'title'}
                          />
                          <Form.Check
                            type="radio"
                            label="Date Added (Newest)"
                            id="newest"
                            value="Date Added (Newest)"
                            name="sort"
                            checked={sortOrder === 'DESC' && sortBy === 'created_at'}
                          />
                          <Form.Check
                            type="radio"
                            label="Date Added (Oldest)"
                            id="oldest"
                            value="Date Added (Oldest)"
                            name="sort"
                            checked={sortOrder === 'ASC' && sortBy === 'created_at'}
                          />
                        </Dropdown.Menu>
                      </Dropdown>
                    </PaginationContainer>
                    <PaginationContainer className="text-right">
                      <span>
                        {conferenceCount === 0 ? <>0 </> : <>1 </>}-{' '}
                        {conferenceCount === 0 ? <>0 </> : <>{loadedEnd} </>}
                        of {conferenceCount}
                      </span>
                      <Button onClick={pageDecrement} variant="basic" className="a_p_dec" disabled={loadedEnd <= count}>
                        <i className="fas fa-angle-left" />
                      </Button>
                      <Button
                        onClick={pageIncrement}
                        variant="basic"
                        className="a_p_inc"
                        disabled={loadedEnd >= conferenceCount}
                      >
                        <i className="fas fa-angle-right" />
                      </Button>
                      <Dropdown>
                        <Dropdown.Toggle variant="basic" id="limitMenu" className="a_limit">
                          <i className="fas fa-ellipsis-v" />
                        </Dropdown.Toggle>

                        <Dropdown.Menu onChange={e => changeCount(e.target.value)}>
                          <Form.Check
                            type="radio"
                            label="10 per page"
                            id="limit_10"
                            value="10"
                            name="count"
                            checked={parseInt(count, 10) === 10}
                          />
                          <Form.Check
                            type="radio"
                            label="25 per page"
                            id="limit_25"
                            value="25"
                            name="count"
                            checked={parseInt(count, 10) === 25}
                          />
                          <Form.Check
                            type="radio"
                            label="50 per page"
                            id="limit_50"
                            value="50"
                            name="count"
                            checked={parseInt(count, 10) === 50}
                          />
                          <Form.Check
                            type="radio"
                            label="100 per page"
                            id="limit_100"
                            value="100"
                            name="count"
                            checked={parseInt(count, 10) === 100}
                          />
                        </Dropdown.Menu>
                      </Dropdown>
                    </PaginationContainer>
                  </div>
                  {loading ? (
                    <ScreenLoader containerHeight="calc(50vh)" />
                  ) : (
                    <>
                      {conferencesList.map(conference => (
                        <CourseCard
                          key={conference.id}
                          item={conference}
                          type="conference"
                          playlist={playlist}
                          onBookmark={onHandleBookmark}
                        />
                      ))}
                      {hasMoreConferences && <div ref={sentinelRef} style={{minHeight: 1}} />}
                      {isConferenceLoadingMore && (
                        <div className="d-flex justify-content-center py-3">
                          <Spinner animation="grow" />
                        </div>
                      )}
                    </>
                  )}
                </Container>
              </CatalogList>
            </Row>
          </Container>
          {!loading && <Footer />}
        </Main>
      </div>
      <PageBottom />
    </div>
  );
});

export default ConferenceCatalog;
